<template>
  <div class="buy-con pdt-4">
    <div class="current-info flex-btw">
      <span>{{ t('available_credits') }}</span>
      <div class="my-credit">
        <img class="credit-icon" :src="getAssetImageUrl('common/icons/credit.svg')" alt="" />
        <strong>{{ formatNumberWithComma(subscriptionStore.currentCredits) }}</strong>
      </div>
    </div>

    <PlanUpgradeBanner @click="moveToPlanInfo" />

    <h1 class="app-page-tit mb-3">{{ t('credit_package') }}</h1>

    <ul class="package-list">
      <li v-for="item in creditPackageList" :key="item">
        <label class="credit-pa">
          <input
            type="radio"
            name="package"
            :checked="selectedPackage?.credit === item.credit"
            @click="handleRadioClick(item)"
          />

          <div class="credit-flex">
            <img class="credit-icon" :src="getAssetImageUrl('common/icons/credit.svg')" alt="credit" />
            <span>{{ formatNumberWithComma(item.credit) }} {{ t('credit') }}</span>
          </div>

          <div class="price">{{ formatCurrency(item.price, subscriptionStore.currency) }}</div>
        </label>
      </li>
    </ul>

    <div class="self-wrap">
      <h2 class="app-page-tit pdt-7 pdb-3">{{ t('enter_manually') }}</h2>
      <div class="self-input-wrap">
        <div class="inr">
          <input
            type="text"
            class="input"
            inputmode="numeric"
            :value="customCredit"
            :placeholder="`${t('enter_credit_amount')}`"
            @input="handleInput"
          />
          <span class="text-mirror">{{ customCredit }}</span>

          <button v-if="customCredit" class="follow-btn" :style="{ left: closeButtonX + 'px' }" @click="resetInput">
            <img :src="getAssetImageUrl('common/icons/close.svg')" alt="" />
          </button>
        </div>
        <div class="self-price">
          {{ t('estimated_payment_amount') }} {{ formatCurrency(inputPrice, subscriptionStore.currency) }}
        </div>
      </div>
    </div>
  </div>

  <div class="buy-btn-wrap">
    <button type="button" class="primary-btn l-btn" :disabled="isLoading || priceNumber === 0" @click="purchaseCredits">
      <span>{{
        t('recharge_with_price', { price: formatCurrency(finalPaymentPrice, subscriptionStore.currency) })
      }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { chargeCredit } from '@/api/apiService';
import PlanUpgradeBanner from '@/components/domains/self-service/payment-settings/PlanUpgradeBanner.vue';
import PopupCompleteCharge from '@/components/domains/self-service/payment-settings/PopupCompleteCharge.vue';
import PopupSmall from '@/components/shared/composite/PopupSmall.vue';
import { usePaymentError } from '@/composables/domains/self-service/usePaymentError';
import { useGtag } from '@/composables/shared/useGtag';
import { usePayment } from '@/composables/shared/usePayment';
import { usePlan } from '@/composables/shared/usePlan';
import { usePopupStore } from '@/stores/popup';
import { useSubscriptionStore } from '@/stores/subscription';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { formatNumberWithComma } from '@/utils/stringUtils.js';

const { t } = useI18n();
const router = useRouter();

const popupStore = usePopupStore();
const subscriptionStore = useSubscriptionStore();

const { getUserPlanCreditPerPrice, calcCeilPrice, formatCurrency } = usePlan();
const { checkPaymentAgreement } = usePayment();
const { handlePaymentError } = usePaymentError();
const { trackEventClick, trackEventPurchase, trackEventFunnel } = useGtag();

const closeButtonX = ref<number>(0);
const isLoading = ref<boolean>(false);

const ratio = ref<number>(0);
const creditPackageList = ref([]);
const mode = ref<'package' | 'custom'>('custom');

const selectedPackage = ref(null);
const customCredit = ref<string>('');

const customCreditNumber = computed(() => {
  return Number(customCredit.value.replace(/[^0-9]/g, '')) || 0;
});

const inputPrice = computed(() => {
  return calcCeilPrice(customCreditNumber.value, subscriptionStore.currency, ratio.value);
});

const finalPaymentPrice = computed<number>(() => {
  if (mode.value === 'package' && selectedPackage.value) {
    return selectedPackage.value.price;
  }

  return calcCeilPrice(customCreditNumber.value, subscriptionStore.currency, ratio.value);
});

const priceNumber = computed<number>(() => {
  if (mode.value === 'package' && selectedPackage.value) {
    return selectedPackage.value.credit;
  }

  return customCreditNumber.value;
});

const handleRadioClick = (item) => {
  mode.value = 'package';
  selectedPackage.value = item;
  customCredit.value = '';
};

const handleInput = (event: Event) => {
  mode.value = 'custom';
  selectedPackage.value = null;

  const numberValue = (event.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
  customCredit.value = numberValue ? formatNumberWithComma(Number(numberValue)) : '';
};

const resetInput = () => {
  customCredit.value = '';
};

const validateCredits = () => {
  if (priceNumber.value < 3000 || priceNumber.value % 100 !== 0) {
    return t('alert_credits_amount');
  }
  return '';
};

const purchaseCredits = async () => {
  if (isLoading.value) return;

  const errorMessage = validateCredits();
  if (errorMessage) {
    popupStore.open(PopupSmall, {
      title: t(errorMessage),
      confirmButton: { text: t('confirm') },
    });
    return;
  }

  isLoading.value = true;
  trackEventFunnel('click_purchase_button');

  try {
    const isAgreed = await checkPaymentAgreement();
    if (!isAgreed) {
      return false;
    }

    const { success, errorCode } = await chargeCredit(priceNumber.value);

    if (!success) {
      popupStore.open(PopupSmall, {
        title: handlePaymentError(errorCode),
        confirmButton: { text: t('confirm') },
      });
      return;
    }

    trackEventFunnel('complete_credit_buy');
    trackEventPurchase(priceNumber.value, subscriptionStore.currency);

    await subscriptionStore.fetchSubscriptionInfo();
    popupStore.closeLast();

    popupStore.open(PopupSmall, {
      contentSlot: h(PopupCompleteCharge),
      payload: priceNumber.value,
      confirmButton: {
        text: t('confirm'),
        action: () => popupStore.closeLast(),
      },
    });
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const moveToPlanInfo = async () => {
  trackEventClick('popup-credit-buy', '구독알아보기 배너');

  popupStore.closeLast();
  setTimeout(() => {
    router.push({ name: 'ss-plan-info' });
  }, 300);
};

const getCreditPerPrice = async () => {
  const result = (await getUserPlanCreditPerPrice(subscriptionStore.currentPlan, subscriptionStore.currency)) ?? [];

  ratio.value = result[0];
  creditPackageList.value = result[1];
};

watch(customCredit, async () => {
  await nextTick();
  const mirror = document.querySelector('.text-mirror') as HTMLElement;

  closeButtonX.value = mirror.offsetWidth + 4;
});

onMounted(() => {
  getCreditPerPrice();
});
</script>

<style scoped lang="scss">
.buy-con {
  width: 100%;
  padding-bottom: 88px;
  .current-info {
    background: $gray10;
    border-radius: $size-m;
    padding: 1rem 1.25rem;
    span {
      font-size: $font-size-psmall;
      opacity: 0.5;
      color: $color-gray-600;
    }
    .my-credit {
      display: flex;
      align-items: center;
      gap: $size-xs;
      strong {
        font-size: $font-size-l;
        font-weight: 500;
      }
    }
  }
  .package-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(3, auto);
    gap: $spacing2;
    li {
      .credit-pa {
        border: 1px solid $color-gray-40;
        padding: 0.75rem;
        border-radius: $size-m;
        font-size: $font-size-psmall;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        input[type='radio'] {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          border: 0;
          overflow: hidden;
          margin: -1px;
          clip-path: inset(50%);
        }
        &:has(input:checked) {
          border: 1px solid $color-navy-100;
          background: $primary-bg;
          .credit-flex {
            span {
              color: $color-navy-100;
            }
          }
          .price {
            background: $color-navy-100;
            color: #fff;
          }
        }
        .credit-flex {
          display: flex;
          align-items: center;
          gap: $size-xs;
          span {
            font-weight: 500;
          }
        }
        .price {
          background: $gray10;
          border-radius: $size-s;
          padding: 0.375rem 0.75rem;
          font-size: $font-size-xs;
          color: $color-gray-500;
        }
      }
    }
  }
  .self-wrap {
    margin-bottom: 4rem;
    .self-input-wrap {
      .inr {
        position: relative;
        width: 100%;
        .input {
          width: 100%;
          padding: 0;
          font-size: $font-size-l;
          font-weight: 600;
          border: none;
          border-bottom: 2px solid $color-gray-900;
          border-radius: 0;
          &:focus {
            border: none !important;
            border-bottom: 2px solid $color-gray-900 !important;
            box-shadow: none !important;
          }
        }
        .text-mirror {
          position: absolute;
          visibility: hidden;
          white-space: pre;
          font-weight: 600;
          font-size: $font-size-l;
          font-family: inherit;
        }
        .follow-btn {
          position: absolute;
          padding: $size-xs;
          top: 50%;
          transform: translateY(-50%);
        }
      }
      .self-price {
        margin-top: $size-s;
        display: inline-block;
        background: $gray10;
        width: auto;
        padding: 0.375rem 0.75rem;
        border-radius: 100px;
        font-size: $font-size-psmall;
        color: $color-gray-500;
      }
    }
  }
}
.credit-icon {
  width: 1.25rem;
  height: 1.25rem;
  object-fit: contain;
}
.floating-btn-wrap {
  padding-top: 0;
}
a {
  text-decoration: none !important;
}

.buy-btn-wrap {
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  padding: 1.25rem 1.5rem;
  background: #fff;
}

@include respond-to(mobile-landscape) {
  .buy-con {
    .package-list {
      grid-template-columns: repeat(1, 1fr);
      grid-template-rows: auto;
    }
  }
}
</style>
